import { createSlice, createAsyncThunk, type PayloadAction } from '@reduxjs/toolkit';
import { searchService } from '@/services/searchService';
import { storageService } from '@/services/storageService';
import { tabFeedService } from '@/services/tabFeedService';
import type {
  AssetCard,
  SearchRequest,
  SearchFilters,
  BeeHiveContentType,
  SearchAssetDetailResponse,
  TranscriptPhrase,
  MatchedSegment,
  SectionFacet,
  SearchMode,
  SearchModeTag,
  SectionSlug,
  MatchLabel,
  AssetTypeFilter,
  SearchResponseAssets,
  SearchAdvancedFilters,
} from '@/types';

/** Empty advanced-filter state — nothing constrained. */
const EMPTY_ADVANCED_FILTERS: SearchAdvancedFilters = {
  source_system: [],
  date_from: null,
  date_to: null,
  duration_min_ms: null,
  duration_max_ms: null,
  size_min_bytes: null,
  size_max_bytes: null,
  speaker: null,
};

/** Projects the panel's advanced-filter state to the wire SearchFilters shape, keeping only set values. */
function activeAdvancedFilters(a: SearchAdvancedFilters): Partial<SearchFilters> {
  const f: Partial<SearchFilters> = {};
  if (a.source_system.length) f.source_system = a.source_system;
  if (a.date_from) f.date_from = a.date_from;
  if (a.date_to) f.date_to = a.date_to;
  if (a.duration_min_ms != null) f.duration_min_ms = a.duration_min_ms;
  if (a.duration_max_ms != null) f.duration_max_ms = a.duration_max_ms;
  if (a.size_min_bytes != null) f.size_min_bytes = a.size_min_bytes;
  if (a.size_max_bytes != null) f.size_max_bytes = a.size_max_bytes;
  if (a.speaker && a.speaker.trim()) f.speaker = a.speaker.trim();
  return f;
}

// ─── Mock fallback data (used when API is unavailable) ────────────────────────

const MOCK_RESULTS: AssetCard[] = [
  {
    asset_id: 'mock-1',
    file_name: 'Q3_2025_All_Hands_Keynote.mp4',
    project_name: 'All Hands',
    source_system: 'queenbee_media',
    content_type: 'video',
    file_size_mb: 512,
    duration_ms: 1122000,
    thumbnail_url: '/assets/corporate/c1.jpg',
    storage_url: '/assets/real-value-of-ai.mp4',
    event_type_name: null,
    source_created_at: '2026-04-10T10:00:00Z',
    match_score: 94,
    match_label: 'Exact',
    matched_segments_count: 5,
    matched_in: ['Transcript', 'Description'],
    section_slugs: ['speeches_keynotes', 'new'],
    clip_ranges: [{ start_ms: 5000, end_ms: 18000, score: 0.031, text: 'The real value of AI has been bringing down pre-production cycle times.', speaker: null, window_index: 0 }],
    matched_moments: [{ type: 'activity', timestamp_ms: 5200, text: 'Speaker presenting on stage', score: 0.029 }],
    top_clip_hint: { start_ms: 5000, end_ms: 18000, score: 0.031, text: 'The real value of AI has been bringing down pre-production cycle times.', speaker: null, window_index: 0 },
  },
  {
    asset_id: 'mock-2',
    file_name: 'Ragan_Conference_Growth_Panel.mp4',
    project_name: 'Ragan Conference',
    source_system: 'queenbee_media',
    content_type: 'video',
    file_size_mb: 280,
    duration_ms: 2530000,
    thumbnail_url: '/assets/corporate/c2.jpg',
    storage_url: '/assets/real-value-of-ai.mp4',
    event_type_name: null,
    source_created_at: '2026-04-08T14:00:00Z',
    match_score: 87,
    match_label: 'Strong',
    matched_segments_count: 3,
    matched_in: ['Description'],
    section_slugs: ['interviews'],
    clip_ranges: [],
    matched_moments: [],
    top_clip_hint: null,
  },
  {
    asset_id: 'mock-3',
    file_name: 'CFO_Fireside_Margin_Story.mp4',
    project_name: 'Fireside',
    source_system: 'queenbee_media',
    content_type: 'video',
    file_size_mb: 190,
    duration_ms: 728000,
    thumbnail_url: '/assets/corporate/c3.jpg',
    storage_url: '/assets/real-value-of-ai.mp4',
    event_type_name: null,
    source_created_at: '2026-04-05T09:30:00Z',
    match_score: 76,
    match_label: 'Strong',
    matched_segments_count: 2,
    matched_in: ['Transcript'],
    section_slugs: ['speeches_keynotes'],
    clip_ranges: [],
    matched_moments: [],
    top_clip_hint: null,
  },
  {
    asset_id: 'mock-4',
    file_name: 'Product_Launch_v4.mp4',
    project_name: 'Product',
    source_system: 'queenbee_media',
    content_type: 'video',
    file_size_mb: 95,
    duration_ms: 138000,
    thumbnail_url: '/assets/corporate/c4.jpg',
    storage_url: '/assets/real-value-of-ai.mp4',
    event_type_name: null,
    source_created_at: '2026-04-03T16:00:00Z',
    match_score: 63,
    match_label: 'Moderate',
    matched_segments_count: 1,
    matched_in: ['Description'],
    section_slugs: ['new'],
    clip_ranges: [],
    matched_moments: [],
    top_clip_hint: null,
  },
  {
    asset_id: 'mock-5',
    file_name: 'Investor_Update_Q3.mp4',
    project_name: 'Investor Relations',
    source_system: 'queenbee_media',
    content_type: 'video',
    file_size_mb: 210,
    duration_ms: 510000,
    thumbnail_url: '/assets/corporate/c5.jpg',
    storage_url: '/assets/real-value-of-ai.mp4',
    event_type_name: null,
    source_created_at: '2026-04-01T11:00:00Z',
    match_score: 58,
    match_label: 'Moderate',
    matched_segments_count: 1,
    matched_in: ['Description'],
    section_slugs: [],
    clip_ranges: [],
    matched_moments: [],
    top_clip_hint: null,
  },
  {
    asset_id: 'mock-6',
    file_name: 'Sales_Kickoff_Keynote.mp4',
    project_name: 'Sales',
    source_system: 'queenbee_media',
    content_type: 'video',
    file_size_mb: 340,
    duration_ms: 1335000,
    thumbnail_url: '/assets/corporate/c6.jpg',
    storage_url: '/assets/real-value-of-ai.mp4',
    event_type_name: null,
    source_created_at: '2026-03-28T08:00:00Z',
    match_score: 51,
    match_label: 'Moderate',
    matched_segments_count: 1,
    matched_in: ['Description'],
    section_slugs: ['speeches_keynotes'],
    clip_ranges: [],
    matched_moments: [],
    top_clip_hint: null,
  },
];

const MOCK_TRANSCRIPT: TranscriptPhrase[] = [
  { start_ms: 40,    end_ms: 8000,  speaker: 'Speaker', text: 'The real value of AI, I feel, has been basically bringing down the pre-production cycle from weeks or months to literally hours or days.' },
  { start_ms: 8360,  end_ms: 9600,  speaker: 'Speaker', text: "That's really where it's worked." },
  { start_ms: 9680,  end_ms: 11700, speaker: 'Speaker', text: "I think you're gonna see a ton of video coming out." },
  { start_ms: 11840, end_ms: 21400, speaker: 'Speaker', text: "And somebody mentioned in the chat about, I think Joe said, Hey, if we use a video, it's an AI generated establishing shot, is it gonna kill the value?" },
  { start_ms: 21520, end_ms: 23600, speaker: 'Speaker', text: "If it's done right, no one's gonna know." },
  { start_ms: 23760, end_ms: 27300, speaker: 'Speaker', text: "I think they're gonna know if we have a 100% AI created video, which is" },
  { start_ms: 27480, end_ms: 33000, speaker: 'Speaker', text: "One of the things we stay away from today, which will happen eventually, but I think right now it's just not there." },
];

/** Convert confidence (0–100) to a MatchLabel for mock data */
function confidenceToLabel(confidence: number): MatchLabel {
  if (confidence >= 80) return 'Exact';
  if (confidence >= 60) return 'Strong';
  return 'Moderate';
}

/** Partitions a result page by content_type, mirroring the real API's assets split — empty side is {}, not omitted. */
function partitionAssets(cards: AssetCard[]): SearchResponseAssets {
  const videos = cards.filter((c) => c.content_type === 'video');
  const images = cards.filter((c) => c.content_type === 'image');
  return {
    videos: videos.length ? { results: videos, count: videos.length } : {},
    images: images.length ? { results: images, count: images.length } : {},
  };
}

function buildMockDetail(assetId: string, query: string, card: AssetCard): SearchAssetDetailResponse {
  const mockClips = card.clip_ranges?.length
    ? card.clip_ranges.map((cr) => ({
        start_ms: cr.start_ms,
        end_ms: cr.end_ms,
        confidence: Math.round((cr.score / 0.0333) * 100),
        snippets: [cr.text],
      }))
    : [];

  // Map matched_clips -> matched_segments so TranscriptPanel scrub marks still work
  const derivedSegments: MatchedSegment[] = mockClips.map((mc, i) => ({
    segment_id: `clip-${i}`,
    start_ms: mc.start_ms,
    end_ms: mc.end_ms,
    description: mc.snippets[0] ?? '',
    match_score: mc.confidence,
    match_label: confidenceToLabel(mc.confidence),
  }));

  return {
    asset_id: assetId,
    file_name: card.file_name,
    project_name: card.project_name,
    source_system: card.source_system,
    content_type: card.content_type,
    file_size_mb: card.file_size_mb ?? 0,
    duration_ms: card.duration_ms ?? null,
    thumbnail_url: card.thumbnail_url,
    storage_url: card.storage_url,
    query,
    summary: `AI-generated summary for "${card.file_name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ')}". Matched ${card.matched_segments_count ?? 0} relevant segment(s) to query "${query}".`,
    transcript: MOCK_TRANSCRIPT,
    matched_segments: derivedSegments,
    chapters: [
      { index: 0, start_ms: 0,    title: 'Introduction' },
      { index: 1, start_ms: 30000, title: 'Main content' },
    ],
    matched_clips: mockClips,
    matched_moments: card.matched_moments ?? [],
    asset_sections: card.section_slugs ?? [],
  };
}

// ─── Async Thunks ─────────────────────────────────────────────────────────────

export const executeSearch = createAsyncThunk(
  'search/executeSearch',
  async (payload: SearchRequest, { getState }) => {
    const state = getState() as { search: SearchState };

    // ─ Mode: always derive from live Redux state so we never read a stale closure.
    // Caller may pass an explicit mode only when it has special context (e.g. a
    // section-filter re-search that must stay omni). Tags-based selection is
    // always resolved here from the current activeModeTagIds.
    const derivedMode: SearchMode = (() => {
      const { activeModeTagIds, availableTags } = state.search;
      if (!activeModeTagIds.length) return 'omni';
      const modes = [...new Set(
        availableTags
          .filter((t) => activeModeTagIds.includes(t.id))
          .map((t) => t.search_mode),
      )];
      return modes.length === 1 ? modes[0] : 'omni';
    })();

    // ─ Infer content_type from the derived mode so results are scoped to
    // the relevant signal sources (visual moments only exist in video;
    // transcripts exist in video + audio). This also ensures filters_applied
    // in the response is non-empty when mode chips are selected.
    const inferredContentType = ((): BeeHiveContentType[] | null => {
      switch (derivedMode) {
        case 'moment':      return ['video'];
        case 'clip':
        case 'transcript':  return ['video', 'audio'];
        default:            return null;
      }
    })();

    // ─ Advanced filters (the "Filters" panel): source_system, date range,
    // duration/size bounds, speaker. Only set values are projected onto the wire.
    const advanced = activeAdvancedFilters(state.search.advancedFilters);
    const hasAdvanced = Object.keys(advanced).length > 0;

    // ─ Filters: only build a non-null object when there is something to filter.
    // Merge priority: inferred (lowest) < advanced < payloadFilters < activeSection (highest).
    const section = state.search.activeSection;
    const payloadFilters = payload.filters ?? null;
    const filters: SearchFilters | null =
      section != null || payloadFilters != null || inferredContentType != null || hasAdvanced
        ? {
            ...(inferredContentType != null ? { content_type: inferredContentType } : {}),
            ...advanced,
            ...(payloadFilters ?? {}),
            ...(section != null ? { section } : {}),
          }
        : null;

    // ─ asset_type: explicit payload value wins, else fall back to the
    // user-selected filter in state. Null/absent selection is sent as an
    // explicit 'both' so the payload always carries asset_type.
    const assetType: AssetTypeFilter = payload.asset_type ?? state.search.activeAssetType ?? 'both';

    const request: SearchRequest = {
      ...payload,
      mode: derivedMode,
      filters,
      asset_type: assetType,
      // Engine is engram for every request unless a caller explicitly overrides it.
      engine: payload.engine ?? 'engram',
    };
    try {
      return await searchService.search(request);
    } catch (err) {
      console.warn('[searchSlice] API unavailable — using mock search results', err);
      let filtered = MOCK_RESULTS.filter((a) => {
        if (state.search.activeSection) {
          return a.section_slugs?.includes(state.search.activeSection) ?? false;
        }
        return true;
      });
      // Mirror the real API's intersect-never-widen asset_type semantics
      if (assetType === 'video') filtered = filtered.filter((a) => a.content_type === 'video');
      if (assetType === 'image') filtered = filtered.filter((a) => a.content_type === 'image');

      return {
        results: filtered,
        total: filtered.length,
        limit: payload.limit ?? 15,
        offset: payload.offset ?? 0,
        query: payload.query,
        search_mode: 'omni' as const,
        query_plan: { intents: ['semantic'], semantic_text: payload.query, filters_applied: {} },
        assets: partitionAssets(filtered),
        sections_summary: [
          { slug: 'speeches_keynotes', label: 'Speeches & Keynotes', match_count: 3 },
          { slug: 'interviews',        label: 'Interviews',           match_count: 1 },
          { slug: 'new',               label: 'New',                  match_count: 2 },
        ] as SectionFacet[],
      };
    }
  },
);

export const fetchAssetDetail = createAsyncThunk(
  'search/fetchAssetDetail',
  async (
    { assetId, query, contentType }: { assetId: string; query: string; contentType?: BeeHiveContentType },
    { getState },
  ) => {
    try {
      // Images have no transcript — skip that request entirely rather than
      // firing it and discarding the response.
      const isImage = contentType === 'image';

      const [matchDetail, transcriptRes] = await Promise.allSettled([
        searchService.getMatchDetail(assetId, query),
        isImage
          ? Promise.reject(new Error('skipped: image asset has no transcript'))
          : storageService.getTranscript(assetId),
      ]);

      if (matchDetail.status === 'rejected') throw matchDetail.reason;

      const md = matchDetail.value;

      // Map matched_clips -> matched_segments so TranscriptPanel scrub marks still work.
      // Image-asset matched_clips carry no start_ms/end_ms — they have nothing to scrub to.
      const derivedSegments: MatchedSegment[] = md.matched_clips
        .filter((mc): mc is typeof mc & { start_ms: number; end_ms: number } => mc.start_ms != null && mc.end_ms != null)
        .map((mc, i) => ({
          segment_id: `clip-${i}`,
          start_ms: mc.start_ms,
          end_ms: mc.end_ms,
          description: mc.snippets[0] ?? '',
          match_score: mc.confidence,
          match_label: confidenceToLabel(mc.confidence),
        }));

      const transcript: TranscriptPhrase[] =
        transcriptRes.status === 'fulfilled'
          ? transcriptRes.value.data.phrases.map((p) => ({
              start_ms: p.start_ms,
              end_ms: p.end_ms,
              speaker: p.speaker,
              text: p.text,
            }))
          : [];

      const result: SearchAssetDetailResponse = {
        asset_id: md.asset_id,
        file_name: md.file_name,
        project_name: md.project_name,
        source_system: md.source_system,
        content_type: md.content_type,
        file_size_mb: md.file_size_mb ?? 0,
        duration_ms: null,
        thumbnail_url: md.thumbnail_url,
        storage_url: md.storage_url,
        query: md.query,
        summary: md.summary,
        transcript,
        matched_segments: derivedSegments,
        all_sections: md.all_sections,
        matched_sections: md.matched_sections,
        chapters: md.chapters,
        matched_clips: md.matched_clips,
        matched_moments: md.matched_moments,
        asset_sections: md.asset_sections,
      };
      return result;
    } catch (err) {
      console.warn('[searchSlice] API unavailable — using mock asset detail', err);
      const state = getState() as { search: SearchState };
      const card = state.search.results.find((r) => r.asset_id === assetId) ?? MOCK_RESULTS[0];
      return buildMockDetail(assetId, query, card);
    }
  },
);

/**
 * Fetches a section tile asset via the Storage API (§6.3 + §6.4 + §6.5 in parallel).
 * Maps the response to SearchAssetDetailResponse so the same VideoPlayer /
 * TranscriptPanel / HeroSection components can render it without changes.
 */
export const fetchStorageAsset = createAsyncThunk(
  'search/fetchStorageAsset',
  async (assetId: string) => {
    // Asset first, so we know content_type before deciding whether transcript/
    // segments are even worth requesting — images have neither.
    const assetRes = await storageService.getAsset(assetId).catch((err) => {
      throw new Error(`Storage asset fetch failed: ${err}`);
    });
    const asset = assetRes.data;
    const isImage = asset.content_type === 'image';

    const [transcriptRes, segmentsRes] = await Promise.allSettled([
      isImage ? Promise.reject(new Error('skipped: image asset has no transcript')) : storageService.getTranscript(assetId),
      isImage ? Promise.reject(new Error('skipped: image asset has no segments')) : storageService.getSegments(assetId),
    ]);

    const segments =
      segmentsRes.status === 'fulfilled' ? segmentsRes.value.data.segments : [];

    // §6.4 transcript phrases map 1:1 to TranscriptPhrase shape
    const transcript: TranscriptPhrase[] =
      transcriptRes.status === 'fulfilled'
        ? transcriptRes.value.data.phrases.map((p) => ({
            start_ms: p.start_ms,
            end_ms: p.end_ms,
            speaker: p.speaker,
            text: p.text,
          }))
        : [];

    // Aggregate rich segment fields across all segments
    const scenes = segments.flatMap((s) => s.scene ?? []);
    const activities = segments.flatMap((s) => s.activities ?? []);
    const keyPoints = segments.flatMap((s) => s.key_points ?? []);
    const rawSpeakers = segments.flatMap((s) => s.ai_speakers ?? []);
    const aiSpeakers = [...new Set(rawSpeakers)];
    const peopleDescription = segments[0]?.people ?? null;

    // Use the first segment's description as the asset summary (full prose)
    const summary = segments[0]?.description ?? null;

    const result: SearchAssetDetailResponse = {
      asset_id: asset.asset_id,
      file_name: asset.file_name,
      project_name: asset.project_name,
      source_system: asset.source_system,
      content_type: asset.content_type,
      file_size_mb: asset.file_size_bytes != null ? asset.file_size_bytes / (1024 * 1024) : 0,
      duration_ms: asset.duration_ms ?? asset.cu?.total_duration_ms ?? null,
      thumbnail_url: asset.thumbnail_url,
      storage_url: asset.storage_url,
      query: '',
      summary,
      transcript,
      matched_segments: [],
      all_sections: {
        scenes,
        activities,
        key_points: keyPoints,
        ai_speakers: aiSpeakers,
        people_description: peopleDescription,
      },
    };

    return result;
  },
);

/**
 * Fetches a full asset via GET /api/v3/assets/{id}/detail.
 * Used for dedicated asset pages where no search query context is available.
 * Falls back to fetchStorageAsset if the v3 endpoint fails.
 */
export const fetchAssetDetailFull = createAsyncThunk(
  'search/fetchAssetDetailFull',
  async (assetId: string, { dispatch }) => {
    try {
      const detail = await searchService.getAssetDetail(assetId);
      const result: SearchAssetDetailResponse = {
        asset_id: detail.asset_id,
        file_name: detail.file_name,
        project_name: detail.project_name,
        source_system: detail.source_system,
        content_type: detail.content_type,
        file_size_mb: detail.file_size_mb ?? 0,
        duration_ms: detail.duration_ms,
        thumbnail_url: detail.thumbnail_url,
        storage_url: detail.storage_url,
        query: '',
        summary: detail.summary,
        transcript: detail.full_transcript,
        matched_segments: [],
        chapters: detail.chapters,
        matched_clips: [],
        matched_moments: [],
        asset_sections: detail.sections,
        all_sections: {
          scenes: detail.all_sections.scenes,
          activities: detail.all_sections.activities,
          key_points: detail.all_sections.key_points,
          ai_speakers: detail.all_sections.ai_speakers,
          people_description: detail.all_sections.people_description,
        },
      };
      return result;
    } catch {
      // Fall back to storage API if v3 detail is unavailable
      const res = await dispatch(fetchStorageAsset(assetId)).unwrap();
      return res;
    }
  },
);

// ─── Search Tags ─────────────────────────────────────────────────────────────
//
// One chip per distinct API signal. Selecting chip(s) → deriveSearchMode picks
// the most specific mode; mixed signals → omni (all three parallel queries).
//
// Mode reference (from omni-search-v3-api-guide):
//   omni       – Segments + transcript windows + visual moments (default)
//   asset      – Segment descriptions only (fast, no timestamps)
//   clip       – Transcript windows (hybrid vector + BM25, spoken/quote search)
//   moment     – Visual moments only: objects, activities, scenes
//   transcript – Transcript windows, BM25 keyword-only (exact phrase)

// Search-mode tags are a fixed, client-owned set — the search modes the UI
// supports. There is no backend endpoint for these (tags are asset-scoped).
const SEARCH_MODE_TAGS: SearchModeTag[] = [
  {
    id: 'scene',
    label: 'Scene',
    description: 'Search visual scenes (outdoor, office, stage…)',
    search_mode: 'moment',
  },
  {
    id: 'object',
    label: 'Object',
    description: 'Search detected objects in video frames',
    search_mode: 'moment',
  },
  {
    id: 'activity',
    label: 'Activity',
    description: 'Search visual activities (walking, presenting…)',
    search_mode: 'moment',
  },
  {
    id: 'spoken',
    label: 'Spoken',
    description: 'Search the transcript for spoken words & quotes',
    search_mode: 'transcript',
  },
  {
    id: 'description',
    label: 'Description',
    description: 'Search segment descriptions (fast, no clip timestamps)',
    search_mode: 'asset',
  },
];

// ─── Slice ────────────────────────────────────────────────────────────────────

interface SearchState {
  query: string;
  results: AssetCard[];
  total: number;
  loading: boolean;
  error: string | null;
  activeAsset: SearchAssetDetailResponse | null;
  activeAssetLoading: boolean;
  /** ID of the card whose detail is currently being fetched (may differ from activeAsset.asset_id) */
  loadingAssetId: string | null;
  sections_summary: SectionFacet[];
  activeSection: SectionSlug | null;
  searchMode: SearchMode;
  availableTags: SearchModeTag[];
  activeModeTagIds: string[];
  /** User-selected video/image/both filter — null behaves identically to "both" */
  activeAssetType: AssetTypeFilter | null;
  /** Advanced "Filters" panel state — source_system, date/duration/size bounds, speaker */
  advancedFilters: SearchAdvancedFilters;
  /** Same page of `results`, partitioned by content_type — null until a search returns */
  assets: SearchResponseAssets | null;
}

const initialState: SearchState = {
  query: '',
  results: [],
  total: 0,
  loading: false,
  error: null,
  activeAsset: null,
  activeAssetLoading: false,
  loadingAssetId: null,
  sections_summary: [],
  activeSection: null,
  searchMode: 'omni',
  availableTags: SEARCH_MODE_TAGS,
  activeModeTagIds: [],
  activeAssetType: null,
  advancedFilters: EMPTY_ADVANCED_FILTERS,
  assets: null,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    setQuery(state, action: PayloadAction<string>) {
      state.query = action.payload;
    },
    clearSearch(state) {
      state.query = '';
      state.results = [];
      state.total = 0;
      state.loading = false;
      state.error = null;
      state.sections_summary = [];
      state.activeSection = null;
      state.activeModeTagIds = [];
      state.activeAssetType = null;
      state.advancedFilters = EMPTY_ADVANCED_FILTERS;
      state.assets = null;
      // activeAsset is intentionally kept so the last video stays loaded
    },
    clearActiveAsset(state) {
      state.activeAsset = null;
    },
    setActiveSection(state, action: PayloadAction<SectionSlug | null>) {
      state.activeSection = action.payload;
    },
    setAssetType(state, action: PayloadAction<AssetTypeFilter | null>) {
      state.activeAssetType = action.payload;
    },
    setAdvancedFilters(state, action: PayloadAction<Partial<SearchAdvancedFilters>>) {
      state.advancedFilters = { ...state.advancedFilters, ...action.payload };
    },
    clearAdvancedFilters(state) {
      state.advancedFilters = EMPTY_ADVANCED_FILTERS;
    },
    setSearchMode(state, action: PayloadAction<SearchMode>) {
      state.searchMode = action.payload;
    },
    toggleModeTag(state, action: PayloadAction<string>) {
      const id = action.payload;
      // Mode tags are mutually exclusive: selecting a tag replaces any
      // currently active tag; re-selecting the active tag clears it.
      const isActive = state.activeModeTagIds.length === 1 && state.activeModeTagIds[0] === id;
      state.activeModeTagIds = isActive ? [] : [id];
    },
    clearModeTags(state) {
      state.activeModeTagIds = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // executeSearch
      .addCase(executeSearch.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(executeSearch.fulfilled, (state, action) => {
        state.loading = false;
        state.results = action.payload.results;
        state.total = action.payload.total;
        state.sections_summary = action.payload.sections_summary ?? [];
        state.assets = action.payload.assets ?? null;
      })
      .addCase(executeSearch.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Search failed';
      })
      // fetchAssetDetail
      .addCase(fetchAssetDetail.pending, (state, action) => {
        state.activeAssetLoading = true;
        state.loadingAssetId = action.meta.arg.assetId;
      })
      .addCase(fetchAssetDetail.fulfilled, (state, action) => {
        state.activeAssetLoading = false;
        state.loadingAssetId = null;
        state.activeAsset = action.payload;
      })
      .addCase(fetchAssetDetail.rejected, (state) => {
        state.activeAssetLoading = false;
        state.loadingAssetId = null;
      })
      // fetchStorageAsset
      .addCase(fetchStorageAsset.pending, (state, action) => {
        state.activeAssetLoading = true;
        state.loadingAssetId = action.meta.arg;
      })
      .addCase(fetchStorageAsset.fulfilled, (state, action) => {
        state.activeAssetLoading = false;
        state.loadingAssetId = null;
        state.activeAsset = action.payload;
      })
      .addCase(fetchStorageAsset.rejected, (state) => {
        state.activeAssetLoading = false;
        state.loadingAssetId = null;
      })
      // fetchAssetDetailFull
      .addCase(fetchAssetDetailFull.pending, (state, action) => {
        state.activeAssetLoading = true;
        state.loadingAssetId = action.meta.arg;
      })
      .addCase(fetchAssetDetailFull.fulfilled, (state, action) => {
        state.activeAssetLoading = false;
        state.loadingAssetId = null;
        state.activeAsset = action.payload;
      })
      .addCase(fetchAssetDetailFull.rejected, (state) => {
        state.activeAssetLoading = false;
        state.loadingAssetId = null;
      });
  },
});

export const { setQuery, clearSearch, clearActiveAsset, setActiveSection, setAssetType, setAdvancedFilters, clearAdvancedFilters, setSearchMode, toggleModeTag, clearModeTags } = searchSlice.actions;
export default searchSlice.reducer;

/**
 * Fetches the latest video from /sections/new and loads it into the hero
 * (activeAsset) so the first thing a user sees after login is real content.
 */
export const fetchHeroVideo = createAsyncThunk(
  'search/fetchHeroVideo',
  async (_, { dispatch }) => {
    try {
      const res = await tabFeedService.getNew({ limit: 1 });
      const first = res.data.results[0];
      if (first?.asset_id) {
        await dispatch(fetchStorageAsset(first.asset_id));
      }
    } catch {
      // silently fail — default video remains
    }
  },
);
